/**
 * Welcome Empty State
 *
 * Displayed when no tables exist yet in the workspace schema.
 * Provides a friendly introduction and CTA to create the first table.
 */

import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

interface WelcomeEmptyStateProps {
  onCreateTable: () => void;
  isInitializing?: boolean;
}

export function WelcomeEmptyState({
  onCreateTable,
  isInitializing,
}: WelcomeEmptyStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        p: 4,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: "primary.softBg",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <StorageRoundedIcon sx={{ fontSize: 40, color: "primary.500" }} />
      </Box>

      <Typography level="h2" sx={{ mb: 1 }}>
        Welcome to Your Database
      </Typography>

      <Typography
        level="body-lg"
        sx={{ mb: 4, maxWidth: 500, color: "text.secondary" }}
      >
        A database is like a collection of spreadsheets that work together. Each
        &quot;table&quot; is like a spreadsheet with columns for different types
        of data.
      </Typography>

      {/* Feature highlights */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
          mb: 4,
          maxWidth: 600,
        }}
      >
        <FeatureCard
          icon={<TableChartRoundedIcon />}
          title="Tables"
          description="Create tables to organize your data"
        />
        <FeatureCard
          icon={<ViewColumnRoundedIcon />}
          title="Columns"
          description="Add columns for text, numbers, dates, and more"
        />
        <FeatureCard
          icon={<LinkRoundedIcon />}
          title="Relationships"
          description="Connect tables together for powerful data queries"
        />
      </Box>

      <Button
        size="lg"
        startDecorator={<AddRoundedIcon />}
        onClick={onCreateTable}
        loading={isInitializing}
      >
        Create Your First Table
      </Button>

      <Typography level="body-sm" sx={{ mt: 2, color: "text.tertiary" }}>
        Don&apos;t worry, you can always add more tables later
      </Typography>
    </Box>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <Card
      variant="soft"
      sx={{
        p: 2,
        width: 160,
        textAlign: "center",
      }}
    >
      <Box sx={{ color: "primary.500", mb: 1 }}>{icon}</Box>
      <Typography level="title-sm" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography level="body-xs" sx={{ color: "text.secondary" }}>
        {description}
      </Typography>
    </Card>
  );
}

export default WelcomeEmptyState;
